/**
 * The browser half of the self-hosted ALTCHA proof-of-work: brute-force the
 * secret number with SubtleCrypto and encode the solution the server's
 * `AltchaGuard` expects in the `x-altcha-solution` header.
 *
 * Shared by every web front-end that submits a public form (the marketing
 * site's forms, the Client Portal's citizen reports). Each app fetches the
 * challenge from its own API and hands it here.
 */

import { sha256Hex as sha256HexJs } from './sha256';

export interface AltchaChallengePayload {
  algorithm: string;
  challenge: string;
  salt: string;
  maxnumber: number;
  signature: string;
}

/** Header the server reads the solution from. */
export const ALTCHA_HEADER = 'x-altcha-solution';

/** SubtleCrypto where there is one (browsers), the plain-JS hash where not (Hermes). */
async function sha256Hex(input: string): Promise<string> {
  const subtle = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (!subtle) return sha256HexJs(input);
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Find the number whose `SHA256(salt + number)` matches the target challenge. */
async function findNumber(c: AltchaChallengePayload): Promise<number> {
  for (let n = 0; n <= c.maxnumber; n++) {
    if ((await sha256Hex(c.salt + n)) === c.challenge) return n;
    // Yield to the event loop periodically so a large work factor can't starve
    // rendering and leave the tab unresponsive while solving on slower devices.
    if (n % 1000 === 999) await new Promise((resolve) => setTimeout(resolve));
  }
  throw new Error('altcha: no solution found within maxnumber');
}

/** Solve a challenge; returns the base64 payload for {@link ALTCHA_HEADER}. */
export async function solveAltchaChallenge(c: AltchaChallengePayload): Promise<string> {
  const number = await findNumber(c);
  return btoa(
    JSON.stringify({
      algorithm: c.algorithm,
      challenge: c.challenge,
      number,
      salt: c.salt,
      signature: c.signature,
    }),
  );
}
